import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listiniAPI, productsAPI } from '../utils/api';
import { ListinoPrezzo, ListinoPrezzoUpsert } from '../types';
import { usePermissions } from '../hooks/usePermissions';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Save,
  Download,
  Search,
  Loader2,
  Star,
  Settings2,
} from 'lucide-react';

interface ListinoFormData {
  nome: string;
  descrizione: string;
  codice: string;
  is_base: boolean;
  valido_da: string;
  valido_a: string;
  is_attivo: boolean;
}

export default function PriceListEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission('listini:create');
  const canEdit = hasPermission('listini:edit');
  const isNew = !id || id === 'new';

  const [formData, setFormData] = useState<ListinoFormData>({
    nome: '',
    descrizione: '',
    codice: '',
    is_base: false,
    valido_da: '',
    valido_a: '',
    is_attivo: true,
  });
  const [prezziPage, setPrezziPage] = useState(1);
  const [prezziSearch, setPrezziSearch] = useState('');
  const [prezziCategoria, setPrezziCategoria] = useState('');
  const [prezziMarca, setPrezziMarca] = useState('');
  const [editedPrezzi, setEditedPrezzi] = useState<Record<string, ListinoPrezzoUpsert>>({});
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkForm, setBulkForm] = useState({
    tipo_modifica: 'percentuale' as 'percentuale' | 'assoluto',
    valore: 0,
    filtro_categoria: '',
    filtro_marca: '',
    arrotondamento: 2,
  });

  // Fetch listino
  const { data: listino, isLoading: isLoadingListino } = useQuery({
    queryKey: ['listino', id],
    queryFn: async () => {
      const response = await listiniAPI.getListino(id!);
      return response.data;
    },
    enabled: !isNew,
  });

  // Fetch prezzi
  const { data: prezziData, isLoading: isLoadingPrezzi } = useQuery({
    queryKey: ['listino-prezzi', id, prezziPage, prezziSearch, prezziCategoria, prezziMarca],
    queryFn: async () => {
      const response = await listiniAPI.getPrezzi(id!, {
        page: prezziPage,
        limit: 50,
        search: prezziSearch || undefined,
        categoria: prezziCategoria || undefined,
        marca: prezziMarca || undefined,
      });
      return response.data;
    },
    enabled: !isNew,
  });

  // Fetch categories and brands for filters
  const { data: categories } = useQuery({
    queryKey: ['product-categories'],
    queryFn: async () => (await productsAPI.getCategories()).data,
  });
  const { data: brands } = useQuery({
    queryKey: ['product-brands'],
    queryFn: async () => (await productsAPI.getBrands()).data,
  });

  // Populate form when listino loads
  useEffect(() => {
    if (listino) {
      setFormData({
        nome: listino.nome,
        descrizione: listino.descrizione || '',
        codice: listino.codice || '',
        is_base: listino.is_base,
        valido_da: listino.valido_da || '',
        valido_a: listino.valido_a || '',
        is_attivo: listino.is_attivo,
      });
    }
  }, [listino]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: ListinoFormData) => listiniAPI.createListino(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product'] });
      queryClient.invalidateQueries({ queryKey: ['listini'] });
      toast.success('Listino creato con successo');
      navigate(`/price-lists/${response.data.id}`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: ListinoFormData) => listiniAPI.updateListino(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product'] });
      queryClient.invalidateQueries({ queryKey: ['listino', id] });
      queryClient.invalidateQueries({ queryKey: ['listini'] });
      toast.success('Listino aggiornato');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento");
    },
  });

  // Save prezzi mutation
  const savePrezziMutation = useMutation({
    mutationFn: (prezzi: ListinoPrezzoUpsert[]) => listiniAPI.upsertPrezzi(id!, prezzi),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product'] });
      queryClient.invalidateQueries({ queryKey: ['listino-prezzi', id] });
      setEditedPrezzi({});
      toast.success('Prezzi salvati');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio prezzi');
    },
  });

  // Bulk update mutation
  const bulkMutation = useMutation({
    mutationFn: () => listiniAPI.bulkUpdatePrezzi(id!, bulkForm),
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product'] });
      queryClient.invalidateQueries({ queryKey: ['listino-prezzi', id] });
      setShowBulkModal(false);
      toast.success(response.data.message);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nella modifica in blocco');
    },
  });

  // Import from products mutation
  const importMutation = useMutation({
    mutationFn: () => listiniAPI.importaDaProdotti(id!),
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product'] });
      queryClient.invalidateQueries({ queryKey: ['listino-prezzi', id] });
      toast.success(response.data.message);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || "Errore nell'importazione");
    },
  });

  const handleSubmitForm = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      ...formData,
      valido_da: formData.valido_da || undefined,
      valido_a: formData.valido_a || undefined,
    };
    if (isNew) {
      createMutation.mutate(payload as any);
    } else {
      updateMutation.mutate(payload as any);
    }
  };

  const handlePriceChange = (prezzo: ListinoPrezzo, field: 'prezzo_unitario' | 'prezzo_minimo', value: string) => {
    const numVal = parseFloat(value) || 0;
    setEditedPrezzi((prev) => ({
      ...prev,
      [prezzo.prodotto_id]: {
        prodotto_id: prezzo.prodotto_id,
        prezzo_unitario: field === 'prezzo_unitario' ? numVal : (prev[prezzo.prodotto_id]?.prezzo_unitario ?? prezzo.prezzo_unitario),
        prezzo_minimo: field === 'prezzo_minimo' ? numVal : (prev[prezzo.prodotto_id]?.prezzo_minimo ?? prezzo.prezzo_minimo),
      },
    }));
  };

  const handleSavePrezzi = () => {
    const prezziToSave = Object.values(editedPrezzi);
    if (prezziToSave.length === 0) return;
    savePrezziMutation.mutate(prezziToSave);
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);

  const prezzi = prezziData?.data || [];
  const prezziPagination = prezziData?.pagination;
  const hasEdits = Object.keys(editedPrezzi).length > 0;
  const isSaving = createMutation.isPending || updateMutation.isPending;

  if (!isNew && isLoadingListino) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/price-lists')}
            className="inline-flex items-center gap-1 text-gray-500 hover:text-gray-700 text-sm font-medium"
          >
            <ArrowLeft className="h-4 w-4" />
            Listini
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            {isNew ? 'Nuovo Listino' : formData.nome}
          </h1>
          {!isNew && listino?.is_base && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              <Star className="h-3 w-3" />
              Base
            </span>
          )}
        </div>
      </div>

      {/* Metadata Form */}
      <form onSubmit={handleSubmitForm} className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Informazioni Listino</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
            <input
              type="text"
              value={formData.nome}
              onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Codice</label>
            <input
              type="text"
              value={formData.codice}
              onChange={(e) => setFormData({ ...formData, codice: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="lg:col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
            <textarea
              value={formData.descrizione}
              onChange={(e) => setFormData({ ...formData, descrizione: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Valido da</label>
            <input
              type="date"
              value={formData.valido_da}
              onChange={(e) => setFormData({ ...formData, valido_da: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Valido a</label>
            <input
              type="date"
              value={formData.valido_a}
              onChange={(e) => setFormData({ ...formData, valido_a: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-end gap-6">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.is_base}
                onChange={(e) => setFormData({ ...formData, is_base: e.target.checked })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">Listino base</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.is_attivo}
                onChange={(e) => setFormData({ ...formData, is_attivo: e.target.checked })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">Attivo</span>
            </label>
          </div>
        </div>
        <div className="flex justify-end mt-4">
          {(isNew ? canCreate : canEdit) && (
            <button
              type="submit"
              disabled={isSaving}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {isNew ? 'Crea Listino' : 'Salva'}
            </button>
          )}
        </div>
      </form>

      {/* Prices Grid (only for existing listini) */}
      {!isNew && (
        <div className="bg-white rounded-lg shadow">
          {/* Toolbar */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold">Prezzi</h2>
              <div className="flex gap-2">
                {canEdit && (
                  <button
                    onClick={() => importMutation.mutate()}
                    disabled={importMutation.isPending}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-50 text-green-700 rounded-lg hover:bg-green-100 disabled:opacity-50"
                  >
                    {importMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                    Importa da prodotti
                  </button>
                )}
                {canEdit && (
                  <button
                    onClick={() => setShowBulkModal(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100"
                  >
                    <Settings2 className="h-4 w-4" />
                    Modifica in blocco
                  </button>
                )}
                {canEdit && hasEdits && (
                  <button
                    onClick={handleSavePrezzi}
                    disabled={savePrezziMutation.isPending}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {savePrezziMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    Salva prezzi ({Object.keys(editedPrezzi).length})
                  </button>
                )}
              </div>
            </div>
            <div className="flex gap-3 flex-wrap">
              <div className="flex-1 min-w-[180px] relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Cerca prodotto..."
                  value={prezziSearch}
                  onChange={(e) => { setPrezziSearch(e.target.value); setPrezziPage(1); }}
                  className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <select
                value={prezziCategoria}
                onChange={(e) => { setPrezziCategoria(e.target.value); setPrezziPage(1); }}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg"
              >
                <option value="">Tutte le categorie</option>
                {categories?.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select
                value={prezziMarca}
                onChange={(e) => { setPrezziMarca(e.target.value); setPrezziPage(1); }}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg"
              >
                <option value="">Tutte le marche</option>
                {brands?.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
          </div>

          {/* Prices Table */}
          {isLoadingPrezzi ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            </div>
          ) : prezzi.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>Nessun prezzo configurato</p>
              {canEdit && (
                <button
                  onClick={() => importMutation.mutate()}
                  className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  Importa prezzi dai prodotti
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Prodotto</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Codice</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Categoria</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Marca</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Prezzo Base</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Prezzo Listino</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Prezzo Min.</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {prezzi.map((prezzo) => {
                    const edited = editedPrezzi[prezzo.prodotto_id];
                    const currentPrezzo = edited?.prezzo_unitario ?? prezzo.prezzo_unitario;
                    const currentMinimo = edited?.prezzo_minimo ?? prezzo.prezzo_minimo;
                    const isEdited = !!edited;

                    return (
                      <tr key={prezzo.id} className={isEdited ? 'bg-yellow-50' : 'hover:bg-gray-50'}>
                        <td className="px-4 py-2 text-sm font-medium text-gray-900">{prezzo.prodotto_nome}</td>
                        <td className="px-4 py-2 text-sm text-gray-500">{prezzo.prodotto_codice || '-'}</td>
                        <td className="px-4 py-2 text-sm text-gray-500">{prezzo.prodotto_categoria || '-'}</td>
                        <td className="px-4 py-2 text-sm text-gray-500">{prezzo.prodotto_marca || '-'}</td>
                        <td className="px-4 py-2 text-sm text-right text-gray-500">
                          {prezzo.prodotto_prezzo_base != null ? formatCurrency(prezzo.prodotto_prezzo_base) : '-'}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={currentPrezzo}
                            onChange={(e) => handlePriceChange(prezzo, 'prezzo_unitario', e.target.value)}
                            disabled={!canEdit}
                            className="w-28 px-2 py-1 text-sm text-right border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                          />
                        </td>
                        <td className="px-4 py-2 text-right">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={currentMinimo ?? ''}
                            onChange={(e) => handlePriceChange(prezzo, 'prezzo_minimo', e.target.value)}
                            disabled={!canEdit}
                            className="w-28 px-2 py-1 text-sm text-right border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                            placeholder="-"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Prezzi Pagination */}
          {prezziPagination && prezziPagination.pages > 1 && (
            <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200">
              <div className="text-sm text-gray-700">
                Pagina {prezziPagination.page} di {prezziPagination.pages} ({prezziPagination.total} prezzi)
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPrezziPage(prezziPage - 1)}
                  disabled={prezziPage === 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50"
                >
                  Precedente
                </button>
                <button
                  onClick={() => setPrezziPage(prezziPage + 1)}
                  disabled={prezziPage >= prezziPagination.pages}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50"
                >
                  Successivo
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Bulk Update Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Modifica in Blocco</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo modifica</label>
                <select
                  value={bulkForm.tipo_modifica}
                  onChange={(e) => setBulkForm({ ...bulkForm, tipo_modifica: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="percentuale">Percentuale (%)</option>
                  <option value="assoluto">Assoluto (EUR)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valore {bulkForm.tipo_modifica === 'percentuale' ? '(es: +10 o -5)' : '(EUR)'}
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={bulkForm.valore}
                  onChange={(e) => setBulkForm({ ...bulkForm, valore: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Filtra per categoria</label>
                <select
                  value={bulkForm.filtro_categoria}
                  onChange={(e) => setBulkForm({ ...bulkForm, filtro_categoria: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">Tutte</option>
                  {categories?.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Filtra per marca</label>
                <select
                  value={bulkForm.filtro_marca}
                  onChange={(e) => setBulkForm({ ...bulkForm, filtro_marca: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">Tutte</option>
                  {brands?.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Arrotondamento (decimali)</label>
                <input
                  type="number"
                  min="0"
                  max="4"
                  value={bulkForm.arrotondamento}
                  onChange={(e) => setBulkForm({ ...bulkForm, arrotondamento: parseInt(e.target.value) || 2 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowBulkModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annulla
              </button>
              <button
                onClick={() => bulkMutation.mutate()}
                disabled={bulkMutation.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {bulkMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Applica
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
